import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { timeEntriesApi, payPeriodsApi, adminApi, formatDate, formatDateRange } from '../lib/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Calendar as CalendarIcon, 
  Plus, 
  CheckCircle2, 
  XCircle, 
  Clock,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const Dashboard = () => {
  const { user, effectiveUser, impersonating } = useAuth();
  const navigate = useNavigate();
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Form state
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [hours, setHours] = useState('');
  const [project, setProject] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [impersonating]);

  const fetchData = async () => {
    try {
      const [periodRes, entriesRes] = await Promise.all([
        payPeriodsApi.getCurrent(),
        impersonating 
          ? adminApi.getTimeEntries({ userId: impersonating.id })
          : timeEntriesApi.getAll()
      ]);
      setCurrentPeriod(periodRes.data);
      setEntries(entriesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await timeEntriesApi.create({
        date: format(selectedDate, 'yyyy-MM-dd'),
        hours: parseFloat(hours),
        project,
        description
      });
      toast.success('Time entry submitted');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit entry';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedDate(new Date());
    setHours('');
    setProject('');
    setDescription('');
  };

  // Calculate stats
  const currentPeriodEntries = entries.filter(
    e => e.pay_period_start === currentPeriod?.start_date
  );
  const totalHours = currentPeriodEntries.reduce((sum, e) => sum + e.hours, 0);
  const pendingHours = currentPeriodEntries
    .filter(e => e.status === 'pending')
    .reduce((sum, e) => sum + e.hours, 0);
  const approvedHours = currentPeriodEntries
    .filter(e => e.status === 'approved')
    .reduce((sum, e) => sum + e.hours, 0);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200 text-xs">
            <CheckCircle2 className="w-3 h-3 mr-1" aria-hidden="true" />
            Approved
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200 text-xs">
            <XCircle className="w-3 h-3 mr-1" aria-hidden="true" />
            Rejected
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200 text-xs">
            <Clock className="w-3 h-3 mr-1" aria-hidden="true" />
            Pending
          </Badge>
        );
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-slate-500">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="contractor-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
              Welcome, {effectiveUser?.name}
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Current Pay Period: <span className="text-brick-cyan font-medium">{currentPeriod && formatDateRange(currentPeriod.start_date, currentPeriod.end_date)}</span>
            </p>
          </div>

          {!impersonating && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-brick-red hover:bg-[#c4151d] font-medium"
                  data-testid="add-time-entry-btn"
                >
                  <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
                  Log Hours
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">Log Time Entry</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start border-slate-300"
                          data-testid="date-picker-trigger"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4 text-brick-cyan" aria-hidden="true" />
                          {format(selectedDate, 'PPP')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={(date) => date && setSelectedDate(date)}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="hours" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Hours Worked
                    </Label>
                    <Input
                      id="hours"
                      type="number"
                      step="0.5"
                      min="0"
                      max="24"
                      value={hours}
                      onChange={(e) => setHours(e.target.value)}
                      placeholder="8"
                      required
                      data-testid="hours-input"
                      className="border-slate-300 focus:border-brick-cyan focus:ring-1 focus:ring-brick-cyan"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="project" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Project
                    </Label>
                    <Input
                      id="project"
                      type="text"
                      value={project}
                      onChange={(e) => setProject(e.target.value)}
                      placeholder="Project Name"
                      required
                      data-testid="project-input"
                      className="border-slate-300 focus:border-brick-cyan focus:ring-1 focus:ring-brick-cyan"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Description
                    </Label>
                    <Textarea
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="What did you work on?"
                      required
                      data-testid="description-input"
                      className="resize-none border-slate-300 focus:border-brick-cyan focus:ring-1 focus:ring-brick-cyan"
                      rows={3}
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-brick-red hover:bg-[#c4151d] font-medium"
                    disabled={submitting}
                    data-testid="submit-entry-btn"
                  >
                    {submitting ? 'Submitting...' : 'Submit Entry'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card 
            className="bg-white border border-slate-200 shadow-sm hover:border-brick-cyan hover:shadow-md transition-all cursor-pointer"
            onClick={() => navigate('/my-entries')}
            data-testid="total-hours-card"
          >
            <CardContent className="p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total Hours</p>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-3xl font-bold text-slate-900" data-testid="total-hours">
                  {totalHours.toFixed(1)}
                </span>
                <span className="text-sm text-slate-500">hrs</span>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="bg-white border border-slate-200 shadow-sm hover:border-green-500 hover:shadow-md transition-all cursor-pointer"
            onClick={() => navigate('/my-entries')}
            data-testid="approved-hours-card"
          >
            <CardContent className="p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Approved</p>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-3xl font-bold text-green-600" data-testid="approved-hours">
                  {approvedHours.toFixed(1)}
                </span>
                <span className="text-sm text-slate-500">hrs</span>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="bg-white border border-slate-200 shadow-sm hover:border-amber-500 hover:shadow-md transition-all cursor-pointer"
            onClick={() => navigate('/my-entries')}
            data-testid="pending-hours-card"
          >
            <CardContent className="p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Pending</p>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-3xl font-bold text-amber-600" data-testid="pending-hours">
                  {pendingHours.toFixed(1)}
                </span>
                <span className="text-sm text-slate-500">hrs</span>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="bg-white border border-slate-200 shadow-sm hover:border-brick-cyan hover:shadow-md transition-all cursor-pointer"
            onClick={() => navigate('/my-entries')}
            data-testid="entries-count-card"
          >
            <CardContent className="p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Entries</p>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-3xl font-bold text-slate-900" data-testid="entries-count">
                  {currentPeriodEntries.length}
                </span>
                <span className="text-sm text-slate-500">total</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Entries */}
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-200">
            <CardTitle className="font-heading text-lg">Recent Entries</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {currentPeriodEntries.length === 0 ? (
              <div className="text-center py-12">
                <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" aria-hidden="true" />
                <p className="text-slate-500">
                  No entries for this pay period yet.
                </p>
                {!impersonating && (
                  <Button 
                    variant="outline" 
                    className="mt-4 border-brick-cyan text-brick-cyan hover:bg-brick-cyan hover:text-white"
                    onClick={() => setDialogOpen(true)}
                    data-testid="empty-state-add-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
                    Add Your First Entry
                  </Button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {currentPeriodEntries.slice(0, 5).map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors cursor-pointer group"
                    data-testid={`entry-${entry.id}`}
                    onClick={() => navigate('/my-entries')}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-mono text-sm text-brick-cyan" data-testid="entry-date">
                          {formatDate(entry.date)}
                        </span>
                        {getStatusBadge(entry.status)}
                      </div>
                      <p className="font-medium text-slate-900 truncate">{entry.project}</p>
                      <p className="text-sm text-slate-500 line-clamp-1">
                        {entry.description}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                      <div className="text-right">
                        <span className="text-xl font-bold text-slate-900" data-testid="entry-hours">
                          {entry.hours}
                        </span>
                        <span className="text-sm text-slate-500 ml-1">hrs</span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-brick-cyan transition-colors" aria-hidden="true" />
                    </div>
                  </div>
                ))}
                {currentPeriodEntries.length > 5 && (
                  <div className="p-4">
                    <Button 
                      variant="outline" 
                      className="w-full border-brick-cyan text-brick-cyan hover:bg-brick-cyan hover:text-white"
                      onClick={() => navigate('/my-entries')}
                      data-testid="view-all-entries-btn"
                    >
                      View All {currentPeriodEntries.length} Entries
                      <ChevronRight className="w-4 h-4 ml-2" aria-hidden="true" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Dashboard;
